import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ExifReader from "exifreader";
import imageCompression from "browser-image-compression";
import axiosConn from "config/axiosConn";
import * as gps from "../Components/SchoolAdmin/gpsFallbackUtils";
import { useGetSc6SchoolReportQuery, useLookupSc6SchoolMutation, useSubmitSc6ReportMutation, useSubmitSc6SchoolReportMutation } from "../redux/auth/authApi";
import { isSc6Live, isSc6Preview, isSc6ViewOnly, SC6_CAMPAIGN, SC6_LIMITS as L, SC6_LIVE_CAPTION, SC6_NAME, SC6_QUESTIONS as Q, SC6_TITLE, SC6_UNDER } from "../Components/SpecialCampaign6/sc6Config";
import Sc6Deco from "../Components/SpecialCampaign6/Sc6Deco";
import posterImg from "../assets/images/special-campaign-6-banner.jpg";
import ecoClubsLogo from "../assets/images/Logo-2.png";
import moeLogo from "../assets/images/schoolEdu-logo.png";
import samagraLogo from "../assets/images/samagra-logo.png";
import "../Components/SpecialCampaign6/Sc6.css";
import "./SpecialCampaign6.css";

/*
 * /special-campaign-6 — public school upload for Special Campaign 6 (no login).
 * DoSEL format for 28.10.2026:
 *   Enter School UDISE ID → form:
 *     1 Quiz on E-Waste organised in morning assembly                Yes / No
 *     2 No. of E-Waste Items Collected (added 2026-10-22)
 *     3 Collected E-Waste disposed of by handing over to authorised
 *       recycler/refurbisher                                          Yes / No
 *     4 No. of Students Participated
 *     5 Upload geotagged photos (2-3), "Special Campaign 6" banner in
 *       the background
 *   → acknowledgement.
 * Plain form layout (simplified on request). Photos go through
 * /upload-collaboration-file first; a photo without GPS gets this device's
 * location stamped in (gpsFallbackUtils) and is refused if location is denied.
 * Every rule is enforced again on the server (SpecialCampaign6Controller.js).
 * With ?sc6preview=1 before the campaign day, saves go to the preview
 * collection, never the real one.
 * Before the campaign day (no preview) the page is VIEW-ONLY: the UDISE box
 * and the four questions are shown disabled, with SC6_LIVE_CAPTION.
 */

const UDISE_LEN = 11;
const ORDER = ["quizInAssembly", "ewasteItemsCollected", "disposedToRecycler", "studentsParticipated", "photos"];
const STEPS = ["School UDISE ID", "Participation details", "Acknowledgement"];
const PHASES = ["udise", "form", "done"];

// UDISE+ codes are 11 characters and may contain letters: keep letters and
// digits only, upper-cased so the code matches the backend key.
const cleanCode = (v) => String(v || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, UDISE_LEN);
const errMsg = (e, fallback) => {
  const m = e?.data?.message || e?.response?.data?.message;
  if (m) return m;
  // RTK FETCH_ERROR / axios "Network Error": the request never got an answer
  if (e?.status === "FETCH_ERROR" || e?.message === "Network Error") return "Could not reach the server. Please check your internet connection and try again.";
  return e?.message || fallback;
};
const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })
    : "";
const fmtCoord = (lat, lng) =>
  `${Math.abs(lat).toFixed(4)}° ${lat >= 0 ? "N" : "S"}, ${Math.abs(lng).toFixed(4)}° ${lng >= 0 ? "E" : "W"}`;
const draftKey = (u) => `sc6_draft_${u}`;
const blankForm = () => ({ quizInAssembly: "", ewasteItemsCollected: "", disposedToRecycler: "", studentsParticipated: "", photos: [] });
const photoOut = (p) => ({ url: p.url, lat: p.lat, lng: p.lng, geoTagAdded: !!p.geoTagAdded });
const yesNo = (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : "");
// digits only; "0" allowed (a school may have collected no E-Waste items)
const countInput = (v, max = 6) => v.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, max);

function extractGPS(tag, refTag) {
  if (!tag) return NaN;
  let dec = typeof tag.description === "number" ? tag.description : parseFloat(tag.description);
  if (!Number.isFinite(dec) && Array.isArray(tag.value)) {
    const toNum = (x) => (Array.isArray(x) ? (x[1] ? x[0] / x[1] : x[0]) : Number(x));
    const d = toNum(tag.value[0]);
    const m = tag.value.length > 1 ? toNum(tag.value[1]) : 0;
    const s = tag.value.length > 2 ? toNum(tag.value[2]) : 0;
    dec = d + m / 60 + s / 3600;
  }
  if (!Number.isFinite(dec)) return NaN;
  const ref = String(refTag?.description ?? refTag?.value ?? "").trim().toUpperCase();
  if (ref.startsWith("S") || ref.startsWith("W")) dec = -Math.abs(dec);
  return dec;
}

const safeName = (file) => {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const base = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "photo";
  return `${base}_${new Date().toISOString().replace(/[:.]/g, "-")}_sc6.${ext}`;
};

const uploadFiles = async (formData) => {
  const res = await axiosConn("post", "upload-collaboration-file", formData, { headers: { "Content-Type": "multipart/form-data" } });
  const urls = res?.data?.status === "success" ? res.data.data || [] : [];
  if (!urls.length) throw new Error(res?.data?.message || "Upload failed. Please try again.");
  return urls;
};

// Client copy of the server rules: { field: message }. `floor` is the saved
// report on the public (no-login) path: numbers may only go up, Yes may not
// become No, saved photos may not be removed — see LOCK_HINT.
const LOCK_HINT = "To edit these details, please login to the school dashboard.";
const validate = (f, floor) => {
  const out = {};
  if (!f.quizInAssembly) out.quizInAssembly = "Please select Yes or No.";
  const items = Number(f.ewasteItemsCollected);
  if (f.ewasteItemsCollected === "" || !Number.isInteger(items)) out.ewasteItemsCollected = "Enter the number of E-Waste items collected.";
  else if (items < L.itemsMin || items > L.itemsMax) out.ewasteItemsCollected = `Enter a number from ${L.itemsMin} to ${L.itemsMax.toLocaleString("en-IN")}.`;
  if (!f.disposedToRecycler) out.disposedToRecycler = "Please select Yes or No.";
  const n = Number(f.studentsParticipated);
  if (f.studentsParticipated === "" || !Number.isInteger(n)) out.studentsParticipated = "Enter the number of students who participated.";
  else if (n < L.studentsMin || n > L.studentsMax) out.studentsParticipated = `Enter a number from ${L.studentsMin} to ${L.studentsMax.toLocaleString("en-IN")}.`;
  if (f.photos.length < L.photosMin) out.photos = `Upload at least ${L.photosMin} geotagged photos.`;
  if (floor) {
    if (floor.quizInAssembly === "yes" && f.quizInAssembly !== "yes") out.quizInAssembly = LOCK_HINT;
    if (floor.disposedToRecycler === "yes" && f.disposedToRecycler !== "yes") out.disposedToRecycler = LOCK_HINT;
    if (!out.ewasteItemsCollected && items < (floor.ewasteItemsCollected || 0)) out.ewasteItemsCollected = LOCK_HINT;
    if (!out.studentsParticipated && n < (floor.studentsParticipated || 0)) out.studentsParticipated = LOCK_HINT;
  }
  return out;
};

// Printable acknowledgement: its own window with the portal logos, so the
// printout is a clean one-page certificate-style sheet (the page's own print
// CSS used to leave a blank first page and no logos).
const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const printAcknowledgement = ({ ackId, school, udise, form, submittedAt, updated }) => {
  const row = (k, v) => `<tr><th>${esc(k)}</th><td>${v}</td></tr>`;
  const pill = (v) => `<span class="pill ${v === "yes" ? "yes" : "no"}">${esc(yesNo(v))}</span>`;
  const place = [school.block, school.district, school.state].filter(Boolean).join(", ");
  const photos = (form.photos || []).map((p) => `<figure><img src="${esc(p.url)}" alt=""><figcaption>${esc(fmtCoord(p.lat, p.lng))}</figcaption></figure>`).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(SC6_NAME)} — Acknowledgement ${esc(ackId)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Poppins, "Segoe UI", Arial, sans-serif; color: #1b2a3a; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .logos { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding-bottom: 12px; border-bottom: 3px solid #1e7b34; }
  .logos img { height: 52px; width: auto; object-fit: contain; }
  .logos img.moe { height: 60px; }
  .title { margin: 18px 0 2px; font-size: 22px; font-weight: 800; color: #1d4fb3; }
  .under { margin: 0; font-size: 13px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #166534; }
  .sub { margin: 4px 0 0; font-size: 15px; font-weight: 600; color: #166534; }
  .ack { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 18px 0; padding: 14px 18px; border: 1.5px dashed #9fd3ae; border-radius: 12px; background: #f1faf4; }
  .ack b { display: block; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: #5f6f80; }
  .ack .no { font: 700 24px/1.2 ui-monospace, Menlo, monospace; letter-spacing: 0.06em; color: #14532d; }
  .ack .ok { font-size: 15px; font-weight: 700; color: #1e7b34; }
  table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
  th, td { padding: 9px 10px; border-bottom: 1px solid #e3eaf3; vertical-align: top; text-align: left; }
  th { width: 46%; font-weight: 500; color: #5f6f80; }
  td { font-weight: 600; }
  .pill { display: inline-block; padding: 1px 12px; border-radius: 999px; font-size: 13px; }
  .pill.yes { color: #14532d; background: #dcf3e3; } .pill.no { color: #334155; background: #e2e8f0; }
  .photos { display: flex; gap: 10px; margin-top: 16px; }
  figure { margin: 0; width: 31%; } figure img { display: block; width: 100%; aspect-ratio: 4/3; object-fit: cover; border-radius: 8px; border: 1px solid #d9e4df; }
  figcaption { margin-top: 4px; font: 500 10.5px/1.3 ui-monospace, Menlo, monospace; color: #5f6f80; }
  .foot { margin-top: 22px; padding-top: 10px; border-top: 1px solid #e3eaf3; font-size: 11px; color: #7d8ca0; display: flex; justify-content: space-between; }
</style></head><body>
  <div class="logos"><img src="${esc(ecoClubsLogo)}" alt="Eco Clubs for Mission LiFE"><img class="moe" src="${esc(moeLogo)}" alt="Ministry of Education"><img src="${esc(samagraLogo)}" alt="Samagra Shiksha"></div>
  <p class="under">${esc(SC6_UNDER)}</p>
  <h1 class="title">${esc(SC6_NAME)}</h1>
  <p class="sub">${esc(SC6_TITLE)} — Acknowledgement of school participation</p>
  <div class="ack"><div><b>Acknowledgement No.</b><span class="no">${esc(ackId)}</span></div><span class="ok">✔ ${updated ? "Details updated" : "Submitted"} successfully</span></div>
  <table>
    ${row("School", `<strong>${esc(school.school_name)}</strong>${place ? `<br><span style="font-weight:500">${esc(place)}</span>` : ""}`)}
    ${row("UDISE code", esc(udise))}
    ${row(Q.quizInAssembly, pill(form.quizInAssembly))}
    ${row(Q.ewasteItemsCollected, esc(Number(form.ewasteItemsCollected).toLocaleString("en-IN")))}
    ${row(Q.disposedToRecycler, pill(form.disposedToRecycler))}
    ${row(Q.studentsParticipated, esc(Number(form.studentsParticipated).toLocaleString("en-IN")))}
    ${row("Submitted on", esc(fmtDateTime(submittedAt)))}
  </table>
  <div class="photos">${photos}</div>
  <div class="foot"><span>Eco Clubs for Mission LiFE · Department of School Education &amp; Literacy, Ministry of Education</span><span>Printed on ${esc(fmtDateTime(new Date()))}</span></div>
</body></html>`;
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  const go = () => { w.focus(); w.print(); };
  const imgs = Array.from(w.document.images);
  let left = imgs.length;
  if (!left) setTimeout(go, 150);
  else imgs.forEach((im) => { const done = () => { if (--left === 0) setTimeout(go, 150); }; if (im.complete) done(); else { im.onload = done; im.onerror = done; } });
  setTimeout(() => { if (left > 0) { left = 0; go(); } }, 4000);
  return true;
};

const svg = (d, extra = {}) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...extra}>
    {d}
  </svg>
);
const I = {
  school: svg(<><path d="M3 10l9-5 9 5-9 5-9-5z" /><path d="M6.5 12v4.5c0 1.4 2.5 3 5.5 3s5.5-1.6 5.5-3V12" /><path d="M21 10v5" /></>),
  quiz: svg(<><path d="M4 5h16v11H8l-4 4z" /><path d="M9.5 9a2.5 2.5 0 1 1 3.3 2.4c-.5.2-.8.6-.8 1.1v.3M12 15.2h.01" /></>),
  truck: svg(<><path d="M3 6h11v10H3zM14 9h4l3 3v4h-7z" /><circle cx="7" cy="17.5" r="1.8" /><circle cx="17" cy="17.5" r="1.8" /></>),
  box: svg(<><path d="M3.5 8 12 3.5 20.5 8v8.5L12 21l-8.5-4.5z" /><path d="M3.5 8 12 12.5 20.5 8M12 12.5V21" /></>),
  users: svg(<><circle cx="9" cy="8" r="3.2" /><path d="M3 19c.6-3.2 3-5 6-5s5.4 1.8 6 5" /><path d="M16 4.8a3.2 3.2 0 0 1 0 6.4M18 14.4c1.7.7 2.8 2.3 3.1 4.6" /></>),
  camera: svg(<><path d="M4 8h3l1.6-2.4h6.8L17 8h3a1 1 0 0 1 1 1v9.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" /><circle cx="12" cy="13.2" r="3.6" /></>),
  check: svg(<path d="M5 12.5l4.5 4.5L19 7.5" />, { strokeWidth: 2.6 }),
  x: svg(<path d="M7 7l10 10M17 7L7 17" />, { strokeWidth: 2.6 }),
  arrow: svg(<path d="M5 12h14M13 6l6 6-6 6" />, { strokeWidth: 2.4 }),
  search: svg(<><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4.2-4.2" /></>),
  info: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.8h.01" /></>),
  pin: svg(<><path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>),
  download: svg(<><path d="M12 4v11M7 10.5l5 5 5-5" /><path d="M5 20h14" /></>),
  print: svg(<><path d="M7 9V4h10v5M7 17H5a1 1 0 0 1-1-1v-5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5a1 1 0 0 1-1 1h-2" /><rect x="7" y="14" width="10" height="6" rx="1" /></>),
  edit: svg(<><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></>),
  trash: svg(<><path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" /></>),
  lock: svg(<><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7.5a4 4 0 0 1 7.6-1.7" /></>),
  phone: svg(<><rect x="7" y="2.5" width="10" height="19" rx="2.2" /><path d="M11 18.3h2" /></>),
};

// What the form asks — shown on the UDISE step so schools come prepared.
const ASKS = [
  { tone: "blue", icon: I.quiz, text: "Quiz on E-Waste organised in the morning assembly", type: "Yes / No" },
  { tone: "teal", icon: I.box, text: "Number of E-Waste items collected", type: "Number" },
  { tone: "green", icon: I.truck, text: "Collected E-Waste handed over to an authorised recycler/refurbisher", type: "Yes / No" },
  { tone: "amber", icon: I.users, text: "Number of students who participated", type: "Number" },
  { tone: "violet", icon: I.camera, text: `${L.photosMin}–${L.photosMax} geotagged photos with the “${SC6_CAMPAIGN}” banner in the background`, type: "Photos" },
];

function YesNo({ name, value, onChange, invalid, disabled, lockedTo }) {
  return (
    <div className={`sc6-seg${invalid ? " is-invalid" : ""}`} role="radiogroup" aria-disabled={disabled || undefined}>
      {["yes", "no"].map((v) => (
        <label key={v} className={`sc6-seg__opt sc6-seg__opt--${v}${value === v ? " is-on" : ""}${lockedTo && lockedTo !== v ? " is-locked" : ""}`} title={lockedTo && lockedTo !== v ? "To edit this, please login to the school dashboard" : undefined}>
          <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} disabled={disabled || (lockedTo && lockedTo !== v) || undefined} />
          {v === "yes" ? I.check : I.x}
          <span>{yesNo(v)}</span>
        </label>
      ))}
    </div>
  );
}

function Question({ n, id, icon, tone, label, error, children }) {
  return (
    <section id={id} className={`sc6-q${error ? " has-error" : ""}`}>
      <div className="sc6-q__head">
        <span className={`sc6-q__icon sc6-q__icon--${tone}`}>{icon}</span>
        <div className="sc6-q__titles">
          <span className="sc6-q__no">{`Question ${n}`}</span>
          <h3 className="sc6-q__label">
            {label}
            <span className="sc6-req" aria-hidden="true">*</span>
          </h3>
        </div>
      </div>
      <div className="sc6-q__body">{children}</div>
      {error && (
        <p className="sc6-q__err" role="alert">
          {I.info}
          <span>{error}</span>
        </p>
      )}
    </section>
  );
}

/*
 * mode "public" (default): /special-campaign-6, UDISE → form → acknowledgement;
 *   after the first save the form only ADDS (no decrease / No / photo removal).
 * mode "school": /school/special-campaign-6 inside the school dashboard —
 *   the school's own report, prefilled, every change allowed, saved through
 *   the logged-in endpoint (udise from the session).
 */
export default function SpecialCampaign6({ mode = "public" }) {
  const navigate = useNavigate();
  const schoolMode = mode === "school";
  const live = useMemo(() => isSc6Live(), []);
  const viewOnly = useMemo(() => isSc6ViewOnly(), []);
  const preview = useMemo(() => isSc6Preview(), []);
  const [phase, setPhase] = useState("udise"); // udise | form | done
  const [udise, setUdise] = useState("");
  const [lookState, setLookState] = useState("idle"); // idle | loading | found | missing | error
  const [lookMsg, setLookMsg] = useState("");
  const [look, setLook] = useState(null); // { school, schoolToken, report }
  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [busyPhotos, setBusyPhotos] = useState(0);
  const [result, setResult] = useState(null);
  const [lookupSchool] = useLookupSc6SchoolMutation();
  const [submitPublic, { isLoading: savingPublic }] = useSubmitSc6ReportMutation();
  const [submitSchool, { isLoading: savingSchool }] = useSubmitSc6SchoolReportMutation();
  const saving = savingPublic || savingSchool;
  const { data: own, isFetching: ownLoading, error: ownError, refetch: refetchOwn } = useGetSc6SchoolReportQuery(undefined, { skip: !schoolMode });
  const fileRef = useRef(null);
  const lastCode = useRef("");

  // Form and acknowledgement open with their card at the top of the screen
  // (below the fixed portal header on phones), so Submit is in view.
  useEffect(() => {
    if (phase === "udise") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const card = document.querySelector(".sc6-wrap");
    const header = document.querySelector("header");
    const offset = header && getComputedStyle(header).position === "fixed" ? header.offsetHeight + 10 : 70;
    if (card) window.scrollTo({ top: Math.max(0, card.getBoundingClientRect().top + window.scrollY - offset), behavior: "smooth" });
  }, [phase]);

  // school mode: the logged-in school's own report → straight to the form
  useEffect(() => {
    if (!schoolMode || !own?.success || phase !== "udise") return;
    const r = own.report;
    setUdise(own.udise_id);
    setLook({ school: own.school, schoolToken: "", report: r });
    setLookState("found");
    setForm(
      r
        ? {
            quizInAssembly: r.quizInAssembly || "",
            ewasteItemsCollected: r.ewasteItemsCollected != null ? String(r.ewasteItemsCollected) : "",
            disposedToRecycler: r.disposedToRecycler || "",
            studentsParticipated: r.studentsParticipated != null ? String(r.studentsParticipated) : "",
            photos: (r.photos || []).map(photoOut),
          }
        : blankForm()
    );
    setErrors({});
    setBanner("");
    setPhase("form");
  }, [schoolMode, own, phase]);

  // keep an unsent form in this browser, per school (public path only)
  useEffect(() => {
    if (schoolMode || phase !== "form" || !udise) return;
    try {
      window.localStorage.setItem(draftKey(udise), JSON.stringify({ ...form, photos: form.photos.map(photoOut) }));
    } catch (e) {
      /* storage unavailable */
    }
  }, [form, phase, udise]);

  const lookup = async (code) => {
    if (code.length !== UDISE_LEN) {
      setLookState("missing");
      setLookMsg("Please enter the 11-character UDISE code.");
      return;
    }
    lastCode.current = code;
    setLookState("loading");
    setLook(null);
    setLookMsg("");
    try {
      const res = await lookupSchool(preview ? { udise_id: code, preview: "1" } : { udise_id: code }).unwrap();
      if (lastCode.current !== code) return;
      if (!res?.found) {
        setLookState("missing");
        setLookMsg(res?.message || "No school found with this UDISE code. Please check and try again.");
        return;
      }
      setLook({ school: res.school, schoolToken: res.schoolToken, report: res.report || null });
      setLookState("found");
    } catch (e) {
      if (lastCode.current !== code) return;
      setLookState("error");
      setLookMsg(errMsg(e, "Something went wrong. Please try again."));
    }
  };

  const onUdise = (v) => {
    setUdise(v);
    if (v.length === UDISE_LEN && v !== lastCode.current) lookup(v);
    if (v.length !== UDISE_LEN) {
      lastCode.current = "";
      setLookState("idle");
      setLook(null);
      setLookMsg("");
    }
  };

  const openForm = () => {
    if (lookState !== "found" || !look) return;
    const r = look.report;
    let f = blankForm();
    if (r) {
      f = {
        quizInAssembly: r.quizInAssembly || "",
        ewasteItemsCollected: r.ewasteItemsCollected != null ? String(r.ewasteItemsCollected) : "",
        disposedToRecycler: r.disposedToRecycler || "",
        studentsParticipated: r.studentsParticipated != null ? String(r.studentsParticipated) : "",
        photos: (r.photos || []).map(photoOut),
      };
    } else {
      try {
        const d = JSON.parse(window.localStorage.getItem(draftKey(udise)) || "null");
        if (d) f = { ...f, ...d, photos: Array.isArray(d.photos) ? d.photos.slice(0, L.photosMax) : [] };
      } catch (e) {
        /* ignore */
      }
    }
    setForm(f);
    setErrors({});
    setBanner("");
    setPhase("form");
  };

  const changeSchool = () => {
    setPhase("udise");
    setUdise("");
    lastCode.current = "";
    setLookState("idle");
    setLook(null);
    setResult(null);
  };

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => {
      if (!e[k]) return e;
      const n = { ...e };
      delete n[k];
      return n;
    });
    setBanner("");
  };

  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || busyPhotos) return;
    const room = L.photosMax - form.photos.length;
    if (room <= 0) {
      setErrors((e) => ({ ...e, photos: `You can upload at most ${L.photosMax} photos. Remove one to add another.` }));
      return;
    }
    const picked = files.slice(0, room);
    setBusyPhotos(picked.length);
    setErrors((e) => ({ ...e, photos: undefined }));
    try {
      let coords;
      const prepared = [];
      for (const file of picked) {
        if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) throw new Error(`"${file.name}" is not a JPG or PNG photo.`);
        let lat = NaN;
        let lng = NaN;
        try {
          const tags = await ExifReader.load(await file.arrayBuffer());
          lat = extractGPS(tags?.GPSLatitude, tags?.GPSLatitudeRef);
          lng = extractGPS(tags?.GPSLongitude, tags?.GPSLongitudeRef);
        } catch (e) {
          /* no EXIF */
        }
        let out = file;
        let geoTagAdded = false;
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
          if (coords === undefined) {
            try {
              coords = await gps.getCurrentCoords();
            } catch (err) {
              throw new Error(await gps.getGeoFailureMessage(err));
            }
          }
          lat = coords.latitude;
          lng = coords.longitude;
          geoTagAdded = true;
          try {
            out = await gps.embedGpsExif(file, lat, lng);
          } catch (e) {
            /* keep the original bytes; the coordinates still travel with the form */
          }
        }
        if (out.size > L.fileMaxMb * 1024 * 1024) {
          const blob = await imageCompression(out, { maxSizeMB: L.fileMaxMb - 1, maxWidthOrHeight: 2560, useWebWorker: true, preserveExif: true });
          out = new File([blob], out.name, { type: out.type || file.type });
        }
        prepared.push({
          // name + type follow the actual bytes (embedGpsExif turns a PNG into a JPEG)
          file: new File([out], safeName(out), { type: out.type || file.type }),
          lat,
          lng,
          geoTagAdded,
          preview: URL.createObjectURL(out),
        });
      }
      const fd = new FormData();
      prepared.forEach((p) => fd.append("files", p.file));
      const urls = await uploadFiles(fd);
      const added = urls
        .map((url, j) => prepared[j] && { url, lat: prepared[j].lat, lng: prepared[j].lng, geoTagAdded: prepared[j].geoTagAdded, preview: prepared[j].preview })
        .filter(Boolean);
      setForm((f) => ({ ...f, photos: [...f.photos, ...added].slice(0, L.photosMax) }));
    } catch (err) {
      setErrors((e) => ({ ...e, photos: errMsg(err, "Upload failed. Please try again.") }));
    } finally {
      setBusyPhotos(0);
    }
  };

  const savedPhotoUrls = useMemo(() => new Set(schoolMode ? [] : (look?.report?.photos || []).map((p) => p.url)), [schoolMode, look]);
  const removePhoto = (i) => {
    if (savedPhotoUrls.has(form.photos[i]?.url)) {
      setErrors((e) => ({ ...e, photos: LOCK_HINT }));
      return;
    }
    setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }));
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving || busyPhotos) return;
    const errs = validate(form, schoolMode ? null : look?.report);
    const first = ORDER.find((k) => errs[k]);
    if (first) {
      setErrors(errs);
      setBanner("Please complete the fields marked in red.");
      setTimeout(() => document.getElementById(`sc6-f-${first}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 40);
      return;
    }
    try {
      const fields = {
        quizInAssembly: form.quizInAssembly,
        ewasteItemsCollected: Number(form.ewasteItemsCollected),
        disposedToRecycler: form.disposedToRecycler,
        studentsParticipated: Number(form.studentsParticipated),
        photos: form.photos.map(photoOut),
      };
      const res = schoolMode
        ? await submitSchool(fields).unwrap()
        : await submitPublic({ udise_id: udise, schoolToken: look?.schoolToken || "", ...fields, ...(preview ? { preview: "1" } : {}) }).unwrap();
      try {
        window.localStorage.removeItem(draftKey(udise));
      } catch (e2) {
        /* ignore */
      }
      setResult(res);
      setLook((l) => ({
        ...l,
        report: {
          ackId: res.ackId,
          firstSubmittedAt: res.firstSubmittedAt,
          submittedAt: res.submittedAt,
          quizInAssembly: form.quizInAssembly,
          ewasteItemsCollected: Number(form.ewasteItemsCollected),
          disposedToRecycler: form.disposedToRecycler,
          studentsParticipated: Number(form.studentsParticipated),
          photos: form.photos.map(photoOut),
        },
      }));
      setPhase("done");
    } catch (err) {
      const field = err?.data?.field;
      if (field && ORDER.includes(field)) {
        setErrors({ [field]: err.data.message });
        setTimeout(() => document.getElementById(`sc6-f-${field}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 40);
      }
      setBanner(errMsg(err, "Could not submit right now. Please try again."));
    }
  };

  const saved = look?.report;
  const place = look ? [look.school.block, look.school.district, look.school.state].filter(Boolean).join(", ") : "";

  const answered = [!!form.quizInAssembly, form.ewasteItemsCollected !== "", !!form.disposedToRecycler, form.studentsParticipated !== "", form.photos.length >= L.photosMin].filter(Boolean).length;

  return (
    <div className={`sc6-page${schoolMode ? " sc6-page--school" : ""}`} data-phase={phase}>
      <div className="sc6-head sc6-band">
        <Sc6Deco />
        <div className="sc6-head__inner">
          <div className="sc6-head__text">
            <span className="sc6-chip">{viewOnly ? `View only · ${SC6_UNDER}` : SC6_UNDER}</span>
            <h1 className="sc6-head__title">{SC6_NAME}</h1>
            <p className="sc6-head__subtitle">{SC6_TITLE}</p>
            {viewOnly && <p className="sc6-livenote">{SC6_LIVE_CAPTION}</p>}
            {live && (
              <ol className="sc6-steps" aria-label="Steps">
                {STEPS.map((label, i) => {
                  const at = PHASES.indexOf(phase);
                  const state = i < at ? "is-done" : i === at ? "is-on" : "";
                  return (
                    <li key={label} className={`sc6-steps__item ${state}`} aria-current={i === at ? "step" : undefined}>
                      <span className="sc6-steps__dot">{i < at ? I.check : i + 1}</span>
                      <span className="sc6-steps__label">{label}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <div className="sc6-head__media">
            <div className="sc6-poster sc6-poster--sm sc6-poster--deco">
              <img src={posterImg} alt={`${SC6_NAME}: ${SC6_TITLE}`} />
            </div>
            {/* the photos must show this banner in the background (Q.photosNote) */}
            <a className="sc6-btn sc6-btn--outline sc6-head__download" href={posterImg} download="Special-Campaign-6-Banner.jpg">
              {I.download}
              <span>Download this banner</span>
            </a>
          </div>
        </div>
      </div>

      <div className="sc6-wrap">
        {!live && !viewOnly && (
          <div className="sc6-card sc6-card--center">
            <h2 className="sc6-card__title">This form is not accepting responses right now.</h2>
            <button type="button" className="sc6-btn sc6-btn--outline" onClick={() => navigate("/")}>
              Go to the home page
            </button>
          </div>
        )}

        {/* ── view only: the form as it will look, nothing can be filled ── */}
        {viewOnly && (
          <div className="sc6-card sc6-viewonly">
            <div className="sc6-card__head">
              <span className="sc6-card__icon">{I.lock}</span>
              <div className="sc6-card__headtext">
                <span className="sc6-overline">View only</span>
                <h2 className="sc6-card__title">Participation details form</h2>
              </div>
            </div>

            <div className="sc6-card__body">
              <label className="sc6-vo__label" htmlFor="sc6-vo-udise">
                Enter School UDISE ID
              </label>
              <div className="sc6-vo__udise sc6-inputgroup">
                <span className="sc6-inputgroup__icon">{I.search}</span>
                <input id="sc6-vo-udise" className="sc6-inputgroup__input" placeholder="11-character UDISE code" disabled />
                <button type="button" className="sc6-btn sc6-btn--primary" disabled>
                  Search
                </button>
              </div>
            </div>

            <div className="sc6-card__body sc6-qgrid">
              <div className="sc6-qcol">
                <Question n={1} id="sc6-vo-quizInAssembly" icon={I.quiz} tone="blue" label={Q.quizInAssembly}>
                  <YesNo name="sc6-vo-quizInAssembly" value="" onChange={() => {}} disabled />
                </Question>
                <Question n={2} id="sc6-vo-ewasteItemsCollected" icon={I.box} tone="teal" label={Q.ewasteItemsCollected}>
                  <label className="sc6-num">
                    <input inputMode="numeric" placeholder="0" aria-label={Q.ewasteItemsCollected} disabled />
                    <span>items</span>
                  </label>
                </Question>
                <Question n={3} id="sc6-vo-disposedToRecycler" icon={I.truck} tone="green" label={Q.disposedToRecycler}>
                  <YesNo name="sc6-vo-disposedToRecycler" value="" onChange={() => {}} disabled />
                </Question>
                <Question n={4} id="sc6-vo-studentsParticipated" icon={I.users} tone="amber" label={Q.studentsParticipated}>
                  <label className="sc6-num">
                    <input inputMode="numeric" placeholder="0" aria-label={Q.studentsParticipated} disabled />
                    <span>students</span>
                  </label>
                </Question>
              </div>

              <div className="sc6-qcol">
                <Question n={5} id="sc6-vo-photos" icon={I.camera} tone="violet" label={Q.photos}>
                  <div className="sc6-callout">
                    {I.info}
                    <div>
                      <strong>{Q.photosNote}</strong>
                      <span>Photos must be geotagged. If a photo has no location, this device’s location is added — please allow location access when asked.</span>
                    </div>
                  </div>
                  <button type="button" className="sc6-drop" disabled>
                    <span className="sc6-drop__icon">{I.camera}</span>
                    <strong>Click to choose photos</strong>
                    <span>{`or drag and drop here · JPG or PNG, up to ${L.fileMaxMb} MB each`}</span>
                  </button>
                  <div className="sc6-photos__head">
                    <span>{`0 of ${L.photosMax} photos added`}</span>
                    <span className="sc6-photos__min">{`minimum ${L.photosMin}`}</span>
                  </div>
                </Question>
              </div>
            </div>

            <div className="sc6-card__foot sc6-vo__foot">
              <p className="sc6-livenote">{SC6_LIVE_CAPTION}</p>
              <button type="button" className="sc6-btn sc6-btn--primary sc6-btn--lg sc6-btn--submit" disabled>
                <span>Submit</span>
                {I.arrow}
              </button>
            </div>
          </div>
        )}

        {/* school mode: fetching the school's own report */}
        {live && schoolMode && phase === "udise" && (
          <div className="sc6-card sc6-card--center">
            {ownError ? (
              <>
                <h2 className="sc6-card__title">{errMsg(ownError, "Could not load your school’s details.")}</h2>
                <button type="button" className="sc6-btn sc6-btn--primary" onClick={() => refetchOwn()}>
                  Try again
                </button>
              </>
            ) : (
              <h2 className="sc6-card__title">{ownLoading ? "Loading your school’s details…" : "Preparing the form…"}</h2>
            )}
          </div>
        )}

        {/* ── 1 · UDISE ── */}
        {live && !schoolMode && phase === "udise" && (
          <div className="sc6-card">
            <div className="sc6-finder">
              <span className="sc6-finder__icon">{I.school}</span>
              <span className="sc6-overline">Step 1 of 3</span>
              <h2 className="sc6-card__title sc6-finder__title">Enter School UDISE ID</h2>
              <p className="sc6-finder__sub">Your school’s name, block and district will appear automatically.</p>

              <div className="sc6-finder__box">
                <form
                  className="sc6-search"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (lookState === "found") openForm();
                    else lookup(udise);
                  }}
                >
                  <label className="sc6-sr" htmlFor="sc6-udise">
                    School UDISE code
                  </label>
                  <div className={`sc6-inputgroup${lookState === "missing" || lookState === "error" ? " is-invalid" : ""}${lookState === "found" ? " is-found" : ""}`}>
                    <span className="sc6-inputgroup__icon">{I.search}</span>
                    <input
                      id="sc6-udise"
                      className="sc6-inputgroup__input"
                      value={udise}
                      inputMode="text"
                      autoCapitalize="characters"
                      autoComplete="off"
                      maxLength={UDISE_LEN + 6}
                      placeholder="11-character UDISE code"
                      onChange={(e) => onUdise(cleanCode(e.target.value))}
                      autoFocus
                    />
                    <button type="button" className="sc6-btn sc6-btn--primary" onClick={() => lookup(udise)} disabled={lookState === "loading"}>
                      {lookState === "loading" ? "Searching…" : "Search"}
                    </button>
                  </div>
                  <div className="sc6-digits" aria-hidden="true">
                    <span className="sc6-digits__bar">
                      <i style={{ width: `${(udise.length / UDISE_LEN) * 100}%` }} />
                    </span>
                    <span className="sc6-digits__text">{`${udise.length} of ${UDISE_LEN} characters`}</span>
                  </div>
                </form>

                {(lookState === "missing" || lookState === "error") && (
                  <p className="sc6-alert sc6-alert--error" role="alert">
                    {I.info}
                    <span>{lookMsg}</span>
                  </p>
                )}

                {lookState === "found" && look && (
                  <>
                    <div className="sc6-schoolcard">
                      <span className="sc6-schoolcard__icon">{I.school}</span>
                      <div className="sc6-schoolcard__body">
                        <span className="sc6-schoolcard__tag">
                          {I.check}
                          <span>School found</span>
                        </span>
                        <strong className="sc6-schoolcard__name">{look.school.school_name}</strong>
                        {place && <span className="sc6-schoolcard__place">{place}</span>}
                        <span className="sc6-schoolcard__udise">{`UDISE ${udise}`}</span>
                      </div>
                    </div>
                    {saved && (
                      <p className="sc6-alert sc6-alert--info">
                        {I.info}
                        <span>{`This school already submitted on ${fmtDateTime(saved.submittedAt)} (Acknowledgement No. ${saved.ackId}). You can review and update it.`}</span>
                      </p>
                    )}
                    <div className="sc6-actions">
                      <button type="button" className="sc6-btn sc6-btn--primary sc6-btn--lg" onClick={openForm}>
                        <span>{saved ? "Review & Update" : "Proceed"}</span>
                        {I.arrow}
                      </button>
                      <button type="button" className="sc6-btn sc6-btn--link" onClick={changeSchool}>
                        Not your school? Change the code
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="sc6-card__body sc6-asks">
              <div className="sc6-asks__head">
                <h3 className="sc6-asks__title">Keep these ready</h3>
                <span className="sc6-asks__sub">The form has 5 questions</span>
              </div>
              <ol className="sc6-asks__grid">
                {ASKS.map((a, i) => (
                  <li key={a.tone} className={`sc6-ask sc6-ask--${a.tone}`}>
                    <div className="sc6-ask__top">
                      <span className="sc6-ask__icon">{a.icon}</span>
                      <span className="sc6-ask__no">{`Q${i + 1}`}</span>
                    </div>
                    <p className="sc6-ask__text">{a.text}</p>
                    <span className="sc6-ask__type">{a.type}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="sc6-card__foot sc6-trust">
              <span>
                {I.lock}
                <span>No login needed</span>
              </span>
              <span>
                {I.phone}
                <span>Works on mobile phones</span>
              </span>
              <span>
                {I.edit}
                <span>Submitted details can be updated</span>
              </span>
            </div>
          </div>
        )}

        {/* ── 2 · form ── */}
        {live && phase === "form" && look && (
          <form className="sc6-card" onSubmit={onSubmit} noValidate>
            <div className="sc6-card__head">
              <span className="sc6-card__icon">{I.school}</span>
              <div className="sc6-card__headtext">
                <span className="sc6-overline">Step 2 of 3 · Participation details</span>
                <h2 className="sc6-card__title sc6-card__title--school">{look.school.school_name}</h2>
                <span className="sc6-card__meta">{[`UDISE ${udise}`, look.school.district, look.school.state].filter(Boolean).join(" · ")}</span>
              </div>
              {!schoolMode && (
                <button type="button" className="sc6-btn sc6-btn--ghost" onClick={changeSchool}>
                  Change school
                </button>
              )}
            </div>

            {!schoolMode && saved && (
              <div className="sc6-lockbar" role="note">
                {I.lock}
                <div>
                  <strong>{`Already submitted (Acknowledgement No. ${saved.ackId}).`}</strong>
                  <span>
                    {"To edit these details, please "}
                    <button type="button" className="sc6-btn sc6-btn--link" onClick={() => window.dispatchEvent(new Event("openSchoolLogin"))}>
                      login to the school dashboard
                    </button>
                    {"."}
                  </span>
                </div>
              </div>
            )}

            <div className="sc6-card__body sc6-qgrid">
              <div className="sc6-qcol">
                <Question n={1} id="sc6-f-quizInAssembly" icon={I.quiz} tone="blue" label={Q.quizInAssembly} error={errors.quizInAssembly}>
                  <YesNo name="quizInAssembly" value={form.quizInAssembly} onChange={(v) => setField("quizInAssembly", v)} invalid={!!errors.quizInAssembly} lockedTo={!schoolMode && saved?.quizInAssembly === "yes" ? "yes" : undefined} />
                </Question>
                <Question n={2} id="sc6-f-ewasteItemsCollected" icon={I.box} tone="teal" label={Q.ewasteItemsCollected} error={errors.ewasteItemsCollected}>
                  <label className={`sc6-num${errors.ewasteItemsCollected ? " is-invalid" : ""}`}>
                    <input
                      inputMode="numeric"
                      autoComplete="off"
                      value={form.ewasteItemsCollected}
                      placeholder={`${L.itemsMin}–${L.itemsMax}`}
                      aria-label={Q.ewasteItemsCollected}
                      onChange={(e) => setField("ewasteItemsCollected", countInput(e.target.value, 4))}
                    />
                    <span>items</span>
                  </label>

                </Question>
                <Question n={3} id="sc6-f-disposedToRecycler" icon={I.truck} tone="green" label={Q.disposedToRecycler} error={errors.disposedToRecycler}>
                  <YesNo name="disposedToRecycler" value={form.disposedToRecycler} onChange={(v) => setField("disposedToRecycler", v)} invalid={!!errors.disposedToRecycler} lockedTo={!schoolMode && saved?.disposedToRecycler === "yes" ? "yes" : undefined} />
                </Question>
                <Question n={4} id="sc6-f-studentsParticipated" icon={I.users} tone="amber" label={Q.studentsParticipated} error={errors.studentsParticipated}>
                  <label className={`sc6-num${errors.studentsParticipated ? " is-invalid" : ""}`}>
                    <input
                      inputMode="numeric"
                      autoComplete="off"
                      value={form.studentsParticipated}
                      placeholder={`${L.studentsMin}–${L.studentsMax}`}
                      aria-label={Q.studentsParticipated}
                      onChange={(e) => setField("studentsParticipated", countInput(e.target.value, 4))}
                    />
                    <span>students</span>
                  </label>

                </Question>
              </div>

              <div className="sc6-qcol">
                <Question n={5} id="sc6-f-photos" icon={I.camera} tone="violet" label={Q.photos} error={errors.photos}>
                  <div className="sc6-callout">
                    {I.info}
                    <div>
                      <strong>{Q.photosNote}</strong>
                      <span>Photos must be geotagged. If a photo has no location, this device’s location is added — please allow location access when asked.</span>
                    </div>
                  </div>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png" multiple hidden onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
                  {form.photos.length < L.photosMax && (
                    <button
                      type="button"
                      className={`sc6-drop${busyPhotos ? " is-busy" : ""}${errors.photos ? " is-invalid" : ""}`}
                      onClick={() => fileRef.current?.click()}
                      disabled={!!busyPhotos}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        addPhotos(e.dataTransfer.files);
                      }}
                    >
                      <span className="sc6-drop__icon">{I.camera}</span>
                      <strong>{busyPhotos ? "Uploading…" : "Click to choose photos"}</strong>
                      <span>{`or drag and drop here · JPG or PNG, up to ${L.fileMaxMb} MB each`}</span>
                    </button>
                  )}
                  <div className="sc6-photos__head">
                    <span>{`${form.photos.length} of ${L.photosMax} photos added`}</span>
                    {form.photos.length < L.photosMin && <span className="sc6-photos__min">{`minimum ${L.photosMin}`}</span>}
                  </div>
                  {form.photos.length > 0 && (
                    <ul className="sc6-photos">
                      {form.photos.map((p, i) => (
                        <li key={p.url} className="sc6-photo">
                          <img src={p.preview || p.url} alt={`Campaign upload ${i + 1}`} />
                          {savedPhotoUrls.has(p.url) ? (
                            <span className="sc6-photo__locked" title="To edit this, please login to the school dashboard">{I.lock}</span>
                          ) : (
                            <button type="button" className="sc6-photo__remove" onClick={() => removePhoto(i)} aria-label={`Remove photo ${i + 1}`}>
                              {I.trash}
                            </button>
                          )}
                          <div className="sc6-photo__meta">
                            {I.pin}
                            <span>{fmtCoord(p.lat, p.lng)}</span>
                          </div>
                          {p.geoTagAdded && <span className="sc6-photo__added">Device location</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </Question>
              </div>
            </div>

            <div className="sc6-card__foot">
              <div className="sc6-progress">
                <span className="sc6-progress__text">{`${answered} of ${ORDER.length} answered`}</span>
                <span className="sc6-progress__bar" aria-hidden="true">
                  <i style={{ width: `${(answered / ORDER.length) * 100}%` }} />
                </span>
              </div>
              {banner && (
                <p className="sc6-foot__err" role="alert">
                  {banner}
                </p>
              )}
              <button type="submit" className="sc6-btn sc6-btn--primary sc6-btn--lg sc6-btn--submit" disabled={saving || !!busyPhotos}>
                <span>{saving ? "Submitting…" : saved ? "Update" : "Submit"}</span>
                {!saving && I.arrow}
              </button>
            </div>
          </form>
        )}

        {/* ── 3 · acknowledgement ── */}
        {live && phase === "done" && result && look && (
          <div className="sc6-card" id="sc6-ack">
            <div className="sc6-card__head sc6-card__head--success">
              <span className="sc6-card__icon sc6-card__icon--success">{I.check}</span>
              <div className="sc6-card__headtext">
                <span className="sc6-overline">Step 3 of 3 · Acknowledgement</span>
                <h2 className="sc6-card__title">{result.updated ? "Details updated successfully" : "Submitted successfully"}</h2>
                <span className="sc6-card__meta">{`Thank you. Your school’s participation details for ${SC6_NAME} have been recorded.`}</span>
              </div>
              <div className="sc6-ackno">
                <span>Acknowledgement No.</span>
                <strong>{result.ackId}</strong>
              </div>
            </div>

            <div className="sc6-card__body">
              {result.preview && <p className="sc6-alert sc6-alert--warn">Preview entry — saved separately from the campaign data.</p>}
              <dl className="sc6-summary">
                <div>
                  <dt>Campaign</dt>
                  <dd>{`${SC6_NAME} (${SC6_UNDER}): ${SC6_TITLE}`}</dd>
                </div>
                <div>
                  <dt>School</dt>
                  <dd>{[look.school.school_name, look.school.district, look.school.state].filter(Boolean).join(", ")}</dd>
                </div>
                <div>
                  <dt>UDISE code</dt>
                  <dd>{udise}</dd>
                </div>
                <div>
                  <dt>{Q.quizInAssembly}</dt>
                  <dd>
                    <span className={`sc6-pill sc6-pill--${form.quizInAssembly}`}>{yesNo(form.quizInAssembly)}</span>
                  </dd>
                </div>
                <div>
                  <dt>{Q.ewasteItemsCollected}</dt>
                  <dd>{Number(form.ewasteItemsCollected).toLocaleString("en-IN")}</dd>
                </div>
                <div>
                  <dt>{Q.disposedToRecycler}</dt>
                  <dd>
                    <span className={`sc6-pill sc6-pill--${form.disposedToRecycler}`}>{yesNo(form.disposedToRecycler)}</span>
                  </dd>
                </div>
                <div>
                  <dt>{Q.studentsParticipated}</dt>
                  <dd>{Number(form.studentsParticipated).toLocaleString("en-IN")}</dd>
                </div>
                <div>
                  <dt>Submitted on</dt>
                  <dd>{fmtDateTime(result.submittedAt)}</dd>
                </div>
              </dl>
              <ul className="sc6-photos sc6-photos--ack">
                {form.photos.map((p, i) => (
                  <li key={p.url} className="sc6-photo">
                    <img src={p.preview || p.url} alt={`Campaign upload ${i + 1}`} />
                    <div className="sc6-photo__meta">
                      {I.pin}
                      <span>{fmtCoord(p.lat, p.lng)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="sc6-card__foot sc6-card__foot--end">
              <button type="button" className="sc6-btn sc6-btn--link" onClick={() => navigate(schoolMode ? "/school" : "/")}>
                {schoolMode ? "Back to the dashboard" : "Go to the home page"}
              </button>
              <button type="button" className="sc6-btn sc6-btn--outline" onClick={() => setPhase("form")}>
                {I.edit}
                <span>Edit details</span>
              </button>
              <button type="button" className="sc6-btn sc6-btn--primary sc6-btn--lg" onClick={() => { if (!printAcknowledgement({ ackId: result.ackId, school: look.school, udise, form, submittedAt: result.submittedAt, updated: result.updated })) setBanner("Please allow pop-ups for this site to print the acknowledgement."); }}>
                {I.print}
                <span>Print acknowledgement</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
